import {
  createTicket,
  putPhoto,
  requestIncidentPhotoUploadUrls,
  requestPhotoUploadUrls,
} from '@jq4race/api-client';
import {
  type CreateTicketRequest,
  type IncidentPhotoUploadUrlsResponse,
  OFFLINE_HEADERS,
  type PhotoUploadUrlsResponse,
} from '@jq4race/shared';

import { computeItemDeadlineMs } from '../../offline/domain/item-deadline.js';
import type { OutboxHandlerContext } from '../../offline/domain/outbox-handler.js';
import type { OutboxItem } from '../../offline/domain/outbox-item.js';
import { anySignal, timeoutSignal } from '../../offline/infrastructure/abort-signal-compat.js';
import type { DexieBlobStore } from '../../offline/infrastructure/dexie-blob-store.js';
import type { DexieOutboxRepository } from '../../offline/infrastructure/dexie-outbox.repository.js';

/**
 * buildRealCheckinContext (coordinator finding #1 — minimal foreground
 * drainer) — the REAL `OutboxHandlerContext` for `checkinCreateHandler`,
 * wiring it to actual Dexie storage and the real api-client. Called ONCE
 * PER ITEM by the drainer (`ForegroundDrainerDeps.buildContext`), so each
 * item gets its OWN freshly-requested presigned upload targets (design:
 * "requests fresh presigned tokens only at sync" — a 14-minute-old cached
 * token from a previous item must never be reused for a different one).
 *
 * Third adversarial review finding H1 — now also takes the `item` itself
 * (the drainer's own `buildContext: (item) => ...` was previously ignoring
 * it entirely): every request this context makes — including the
 * photo/incident-photo upload-URL requests, which used to send NO item
 * context at all — carries the item's OWN `X-Location-Id` (never the
 * operator's CURRENT ambient location) and the offline-replay headers
 * (Slice 5 allowlists this route for the suspension bypass too).
 */
export function buildRealCheckinContext(
  blobStore: DexieBlobStore,
  /**
   * Fourth adversarial review, MEDIUM #2 — needed to implement
   * `persistUploadedPhotoKey` (writes `item.uploadedPhotoKeys` durably so a
   * retry can reuse a still-valid key instead of re-uploading).
   */
  repo: Pick<DexieOutboxRepository, 'get' | 'update'>,
  item: OutboxItem,
): OutboxHandlerContext {
  let targets: PhotoUploadUrlsResponse['uploads'] | null = null;
  let incidentTargets: IncidentPhotoUploadUrlsResponse['uploads'] | null = null;
  let incidentTargetIndex = 0;
  // Fifth adversarial review, finding 2 — the server's token TTL starts at
  // URL ISSUANCE, so each batch remembers when its upload URLs arrived.
  let targetsIssuedAtMs = 0;
  let incidentTargetsIssuedAtMs = 0;

  /**
   * SYNC_REQUEST_TIMEOUT_MS (second adversarial review HIGH #5) — a stalled
   * fetch (dead connection, backend hang) must never hold the drainer's
   * single-flight lock forever; aborting after this long lets the item fall
   * back to `pending` via the network_error retry path (classify-sync-error).
   * Fresh per REQUEST (a new one is minted every call). Uses `timeoutSignal`
   * (falls back for Safari/iOS < 16.0 — see abort-signal-compat.ts) instead
   * of calling `AbortSignal.timeout` directly.
   */
  const perRequestSignal = () => timeoutSignal(SYNC_REQUEST_TIMEOUT_MS);

  // Third adversarial review finding H2 — a per-request timeout alone still
  // lets a worst-case item (many slow-but-not-quite-timed-out requests: a
  // photo-url request, N photo PUTs, an incident-url request, M incident
  // PUTs, then createTicket) hold the drainer's single-flight lock for
  // several minutes. ONE deadline, created HERE (once per item, since
  // `buildRealCheckinContext` itself is called once per item), combined
  // with every individual request's own per-request signal below.
  //
  // Fourth adversarial review, MEDIUM #2 — a FIXED 150s deadline meant an
  // item with 4 mandatory + N incident photos on a slow link could never
  // finish uploading everything within the window — SCALES with the item's
  // OWN photo count instead (`computeItemDeadlineMs`, offline/domain —
  // shared with `foreground-drainer.ts`'s `STUCK_SYNCING_RESET_THRESHOLD_MS`
  // sizing, LOW #3).
  const itemDeadlineSignal = timeoutSignal(
    computeItemDeadlineMs(item.photoRefs.length + item.incidentPhotoRefs.length),
  );

  function requestSignal(): AbortSignal {
    // `anySignal` falls back for Safari/iOS < 17.4 — see abort-signal-compat.ts.
    return anySignal([itemDeadlineSignal, perRequestSignal()]);
  }

  /**
   * Fourth adversarial review, MEDIUM #2 — the presigned-PUT/create-ticket
   * upload target response carries NO `expiresAt` field at all (neither
   * `PhotoUploadTargetSchema` nor `IncidentPhotoUploadTargetSchema` on
   * `packages/shared`) — this is this context's own ESTIMATE, mirroring
   * `RemotePhotoSink`'s already-documented assumption that a presigned PUT
   * URL (and the token it backs) is good for 15 minutes from issuance.
   * `checkin-create.handler.ts`'s own `KEY_REUSE_SAFETY_MARGIN_MS` is what
   * actually protects against this estimate being slightly optimistic —
   * this function does NOT subtract a margin itself.
   */
  function estimatedTokenExpiry(issuedAtMs: number): string {
    return new Date(issuedAtMs + PRESIGNED_URL_TTL_MS).toISOString();
  }

  /**
   * The item context every photo/incident-photo upload-url request carries
   * (finding H1) — the create-ticket request's headers are still built by
   * `checkin-create.handler.ts` itself (it already has everything it needs
   * from `item`), so only duplicated here for the two upload-url endpoints,
   * which `OutboxHandlerContext.uploadBlob`/`uploadIncidentBlob`'s own
   * signature (`(role, blob)` / `(blob)`) never threads a headers param
   * through.
   */
  function itemRequestOptions() {
    return {
      headers: {
        [OFFLINE_HEADERS.capturedAt]: item.capturedAt,
        [OFFLINE_HEADERS.clientSentAt]: new Date().toISOString(),
        'X-Location-Id': item.locationId,
      },
      // Second adversarial review HIGH #4 — a 403 on a BACKGROUND drain must
      // never clear the operator's current foreground active location.
      suppressLocationAccessHandling: true,
      signal: requestSignal(),
    };
  }

  return {
    getBlobs: async (ids) => {
      // second adversarial review BLOCKER #1 — fetch EXACTLY the ids the
      // handler asked for (item.photoRefs), never "every blob under this
      // outboxId" (which can include a stale/orphaned row).
      const rows = await blobStore.getByIds(ids);
      return rows.map((row) => ({ id: row.id, role: row.role, blob: row.blob }));
    },

    uploadBlob: async (role, blob) => {
      if (!targets) {
        const response = await requestPhotoUploadUrls(itemRequestOptions());
        targets = response.uploads;
        targetsIssuedAtMs = Date.now();
      }
      const target = targets.find((t) => t.angle === role);
      if (!target) throw new Error(`buildRealCheckinContext: no upload target for angle: ${role}`);

      await putPhoto(target.url, blob, requestSignal());
      return { key: target.key, expiresAt: estimatedTokenExpiry(targetsIssuedAtMs) };
    },

    uploadIncidentBlob: async (blob) => {
      // Third adversarial review finding H3 — incident photos are local
      // blobs now too, uploaded fresh at sync time via their OWN
      // upload-url endpoint (angle-less, matched by call ORDER against
      // `item.incidentPhotoRefs`, never a role/angle key).
      if (!incidentTargets) {
        const response = await requestIncidentPhotoUploadUrls(
          item.incidentPhotoRefs.length,
          itemRequestOptions(),
        );
        incidentTargets = response.uploads;
        incidentTargetsIssuedAtMs = Date.now();
      }
      const target = incidentTargets[incidentTargetIndex];
      incidentTargetIndex += 1;
      if (!target) {
        throw new Error(
          'buildRealCheckinContext: no incident photo upload target left for this item',
        );
      }

      await putPhoto(target.url, blob, requestSignal());
      return { key: target.key, expiresAt: estimatedTokenExpiry(incidentTargetsIssuedAtMs) };
    },

    persistUploadedPhotoKey: async (ref, key, expiresAt) => {
      // Fourth adversarial review, MEDIUM #2 — re-reads the CURRENT row
      // rather than trusting a closed-over snapshot: another ref's own
      // persistUploadedPhotoKey call earlier in the SAME attempt (or a
      // concurrent write from elsewhere) may have already updated
      // `uploadedPhotoKeys` since `item` was captured at the top of this
      // function — a naive merge onto the stale `item` would silently
      // clobber that entry.
      const current = await repo.get(item.id);
      const uploadedPhotoKeys = {
        ...(current?.uploadedPhotoKeys ?? {}),
        [ref]: { key, expiresAt },
      };
      await repo.update(item.id, { uploadedPhotoKeys });
    },

    createTicket: async (body, headers) => {
      // Slice 5 reconciliation — `capturedAt` (set by checkin-create.
      // handler.ts) is now a real, optional field on
      // `CreateTicketRequestSchema` (packages/shared). `body` itself stays
      // `Record<string, unknown>` here (OutboxHandlerContext's own generic
      // signature — a handler builds an arbitrary check-in body, this
      // context just transports it) so a cast is still needed to call the
      // concretely-typed `createTicket()`, but it is no longer masking a
      // genuinely unrecognized field.
      const response = await createTicket(body as CreateTicketRequest, {
        headers,
        suppressLocationAccessHandling: true,
        signal: requestSignal(),
      });
      return {
        id: response.id,
        ticketNumber: response.ticketNumber,
        smsDispatched: response.smsDispatched,
        coveredByMembership: response.coveredByMembership,
        checkedInAt: response.checkedInAt,
        checkinTimeSource: response.checkinTimeSource,
      };
    },

    now: () => new Date(),
  };
}

export const SYNC_REQUEST_TIMEOUT_MS = 30_000;

/**
 * PRESIGNED_URL_TTL_MS — the backend's own documented presigned-PUT/photo-
 * token lifetime (matches `RemotePhotoSink`'s `TARGET_AGE_LIMIT_MS`
 * assumption and the backend's `photo-token-purge-policy.ts` doc comment:
 * "far beyond the 15-minute presigned-PUT TTL itself"). Used ONLY to
 * ESTIMATE `expiresAt` on a freshly uploaded key (finding MEDIUM #2) — the
 * response itself carries no such field.
 */
export const PRESIGNED_URL_TTL_MS = 15 * 60 * 1000;
